import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import {
  applyTestingMetricsEnabled,
  mergeProjectThresholds,
} from '@/features/projects/utils/projectMetricThresholds';
import { queryKeys } from '@/shared/hooks';
import { useProjectByIdQuery } from './projects';
import type { ProjectDto, ProjectMetricThresholds, QueryResult } from '@flowqa/shared';

const resolveCachedProject = ({
  orgId,
  projectId,
  queryClient,
}: {
  orgId: string | null;
  projectId: string | null;
  queryClient: ReturnType<typeof useQueryClient>;
}): ProjectDto | null => {
  if (!projectId) {
    return null;
  }

  const projectById =
    queryClient.getQueryData<ProjectDto | null>(queryKeys.projectById(projectId, orgId)) ?? null;
  if (projectById) {
    return projectById;
  }

  const scopedProjects = queryClient.getQueryData<QueryResult<ProjectDto>>(
    queryKeys.projects(orgId),
  );
  return scopedProjects?.items.find((project) => project.id === projectId) ?? null;
};

export const useProjectMetricThresholds = (
  projectId: string | null,
  queryOptions?: { staleTimeMs?: number; refetchOnWindowFocus?: boolean },
): { thresholds: ProjectMetricThresholds; isLoading: boolean } => {
  const { selectedOrganisationId } = useOrganisationFilter();
  const queryClient = useQueryClient();
  const projectQuery = useProjectByIdQuery(projectId, queryOptions);
  const cachedProject = resolveCachedProject({
    orgId: selectedOrganisationId ?? null,
    projectId,
    queryClient,
  });
  const effectiveProject = projectQuery.data ?? cachedProject;
  const thresholds = useMemo(
    () =>
      applyTestingMetricsEnabled(
        mergeProjectThresholds(effectiveProject?.metricThresholds),
        effectiveProject?.testDataControls.testingMetricsEnabled ?? true,
      ),
    [effectiveProject?.metricThresholds, effectiveProject?.testDataControls.testingMetricsEnabled],
  );
  return { thresholds, isLoading: effectiveProject === null && projectQuery.isLoading };
};
